import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, MapPin, Clock } from "lucide-react";
import { api } from "@/lib/api";
import MasonryGrid from "@/components/MasonryGrid";
import PinItButton, { absoluteUrl } from "@/components/PinItButton";
import usePageMeta, { readingTime } from "@/lib/seo";

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [trending, setTrending] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [bestOfWeek, setBestOfWeek] = useState([]);
  const [posts, setPosts] = useState([]);
  usePageMeta();

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/products?trending=true&limit=8"),
      api.get("/products?featured=true&limit=12"),
      api.get("/products?best_of_week=true&limit=8"),
      api.get("/blog?limit=3"),
    ]).then(([c, t, f, b, p]) => {
      setCategories(c.data);
      setTrending(t.data);
      setFeatured(f.data);
      setBestOfWeek(b.data);
      setPosts(p.data);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative w-full overflow-hidden flex flex-col justify-end min-h-[82vh]"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_gulf-coast-home/artifacts/fpkl4rdh_IMG_8633.png"
          alt="Weathered Gulf Coast jon boat at the marsh dock — Gulf Coast Home Co."
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Subtle bottom-left vignette for text readability; preserves boat logo + marsh tones */}
        <div className="absolute inset-0 bg-gradient-to-tr from-black/65 via-black/20 to-transparent" />
        <div className="relative z-10 w-full px-6 md:px-12 pb-16 md:pb-24 max-w-7xl mx-auto">
          <div className="max-w-2xl gc-fade">
            <span className="text-[#EBAA86] text-xs uppercase tracking-[0.3em] flex items-center gap-2 mb-4">
              <MapPin size={14} strokeWidth={1.8} /> Backwater · Dock · Outdoors
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white font-light tracking-tight leading-[1.02]">
              Built to feel<br/>lived in.
            </h1>
            <p className="mt-6 text-white/90 text-base md:text-lg max-w-lg leading-relaxed">
              Rugged Gulf Coast finds for backwater living, dock life, outdoor gear, and everyday carry.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/category/everyday-carry"
                data-testid="hero-shop-edc-btn"
                className="bg-[#F4F1EA] text-[#2D3032] px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#EBAA86] transition-colors flex items-center gap-2"
              >
                Shop EDC <ArrowRight size={14} strokeWidth={1.8} />
              </Link>
              <Link
                to="/blog"
                data-testid="hero-read-journal-btn"
                className="border border-white/60 text-white px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-white hover:text-[#2D3032] transition-colors flex items-center gap-2"
              >
                Read the Journal <Compass size={14} strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="categories-section">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-2">Featured Collections</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#2D3032] tracking-tight font-normal">
              Shop the Core Collections.
            </h2>
          </div>
          <Link to="/blog" data-testid="all-collections-link" className="text-sm uppercase tracking-[0.18em] text-[#636769] hover:text-[#2D3032]">All journal posts →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group relative overflow-hidden bg-[#2D3032] aspect-[2/3] gc-fade gc-card"
            >
              <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.06] transition-all duration-700 gc-sunwash" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                <PinItButton
                  pageUrl={typeof window !== "undefined" ? `${window.location.origin}/category/${c.slug}` : ""}
                  mediaUrl={c.image}
                  description={`${c.name} — ${c.tagline} | Gulf Coast Home Co.`}
                  testid={`pin-category-${c.slug}`}
                />
                {c.pinterest_board && (
                  <button
                    type="button"
                    data-testid={`view-board-${c.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(c.pinterest_board, "_blank", "noopener,noreferrer");
                    }}
                    className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-[#2D3032] px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold hover:bg-[#2D3032] hover:text-white transition-colors"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z"/></svg>
                    Board
                  </button>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-white">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#EBAA86] mb-1">{c.tagline}</p>
                <h3 className="font-heading text-base md:text-lg font-medium leading-tight">{c.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BEST OF THE WEEK */}
      <section className="bg-[#FFFFFF] border-y border-[#E6E1D6]" data-testid="best-of-week-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#EBAA86] mb-2">Best of the Week</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#2D3032] tracking-tight font-normal">
                Hand-picked, road-tested.
              </h2>
            </div>
          </div>
          <MasonryGrid products={bestOfWeek} />
        </div>
      </section>

      {/* TRENDING */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="trending-section">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-2">Trending Right Now</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#2D3032] tracking-tight font-normal">
              What everyone is pinning.
            </h2>
          </div>
        </div>
        <MasonryGrid products={trending} />
      </section>

      {/* PINTEREST EMBED */}
      <section className="bg-[#F4F1EA] border-t border-[#E6E1D6]" data-testid="pinterest-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-3">Follow the boards</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#2D3032] tracking-tight font-normal mb-4">
              We pin everything we'd actually buy.
            </h2>
            <p className="text-[#636769] leading-relaxed mb-8">
              Saved boards for every category — patio days, garage builds, coastal interiors, dad-life essentials. Save to your own boards, build the home you want.
            </p>
            <a
              href="https://pinterest.com/gulfcoasthomeco"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="follow-pinterest-btn"
              className="inline-flex items-center gap-2 bg-[#2D3032] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#EBAA86] hover:text-[#2D3032] transition-colors"
            >
              Follow on Pinterest <ArrowRight size={14} strokeWidth={1.8} />
            </a>
          </div>
          <div className="bg-white border border-[#E6E1D6] p-6" data-testid="pinterest-embed">
            <a
              href="https://pinterest.com/gulfcoasthomeco"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pinterest-embed-link"
            >
              <div className="grid grid-cols-3 gap-2">
                {[
                  "https://images.unsplash.com/photo-1604014237800-1c9102c219da?w=300",
                  "https://images.unsplash.com/photo-1499696010180-025ef6e1a8f9?w=300",
                  "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=300",
                  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=300",
                  "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=300",
                  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=300",
                ].map((u, i) => (
                  <img key={i} src={u} alt="" className="aspect-square object-cover w-full" loading="lazy" />
                ))}
              </div>
              <p className="text-center text-xs uppercase tracking-[0.2em] text-[#636769] mt-4">@gulfcoasthomeco</p>
            </a>
          </div>
        </div>
      </section>

      {/* FEATURED FULL GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="featured-section">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-2">Featured Picks</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D3032] tracking-tight font-normal">
            The full coastal kit.
          </h2>
        </div>
        <MasonryGrid products={featured} />
      </section>

      {/* FROM THE JOURNAL */}
      {posts.length > 0 && (
        <section className="bg-white border-t border-[#E6E1D6]" data-testid="home-journal-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-[#EBAA86] mb-2">From the Journal</p>
                <h2 className="font-heading text-3xl md:text-4xl text-[#2D3032] tracking-tight font-normal">
                  Roundups, stories, and slow weekends.
                </h2>
              </div>
              <Link to="/blog" data-testid="home-journal-all-link" className="text-sm uppercase tracking-[0.18em] text-[#636769] hover:text-[#2D3032]">All articles →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`home-journal-card-${p.slug}`} className="group block gc-card gc-fade">
                  <div className="aspect-[4/5] overflow-hidden bg-[#F4F1EA] mb-4">
                    <img src={absoluteUrl(p.cover_image)} alt={p.title} loading="lazy" className="w-full h-full object-cover gc-sunwash group-hover:scale-[1.04] transition-transform duration-700" />
                  </div>
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-[#8EAD9E] mb-2">
                    {p.tags?.slice(0,1).map((t) => <span key={t}>#{t}</span>)}
                    <span className="text-[#E6E1D6]">/</span>
                    <span className="flex items-center gap-1.5 text-[#636769]"><Clock size={11} strokeWidth={1.8} /> {readingTime(p.content)} min</span>
                  </div>
                  <h3 className="font-heading text-xl text-[#2D3032] font-medium leading-tight group-hover:text-[#8EAD9E] transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-[#636769] line-clamp-2 leading-relaxed">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
